import { Menu, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import ConfigSVG from '@/assets/icons/config.svg';
import LockSVG from '@/assets/icons/lock.svg';
import LogoutSVG from '@/assets/icons/logout.svg';
import { Switch } from '@/components/common/switch';
import { useSettings } from '@/features/user-settings';
import { useRoute } from '@/hooks/use-route';
import { Theme } from '@/utils/types';

const profileAvatarColorMatrix = {
  [Theme.Beer]: '#008A00',
  [Theme.Wine]: '#EF426F',
  [Theme.Ethanol]: '#42c6ef',
};

export const ProfileDropdown = ({
  onLogout,
  onSettings,
  onPasswordChange,
}: {
  onLogout: () => void;
  onSettings: () => void;
  onPasswordChange: () => void;
}) => {
  const { t } = useTranslation();
  const { routeToHome } = useRoute();
  const { setSettings, settings } = useSettings();

  // update demo mode and hard-refresh the webapp to clear any remaining demo state
  const handleDemo = (state: boolean) => {
    setSettings({ ...settings, isDemoMode: state });
    routeToHome();
    window.location.reload();
  };

  return (
    <Menu as="div">
      <Menu.Button>
        <ProfileAvatarIcon />
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 mt-2 w-52 origin-top-right rounded-md bg-white ring-1 ring-black ring-opacity-5 focus:outline-none py-4 shadow-[0px_0px_30px_rgba(0,0,0,0.1)]">
          <Menu.Item>
            {({ active }) => (
              <button
                onClick={onSettings}
                className={`${
                  active ? `bg-gray` : 'text-gray-900'
                } group flex w-full items-center px-6 py-2 text-base font-medium gap-2`}
              >
                <img src={ConfigSVG} alt="config" /> {t('header.settings')}
              </button>
            )}
          </Menu.Item>
          <Menu.Item>
            {({ active }) => (
              <button
                onClick={onPasswordChange}
                className={`${
                  active ? `bg-gray` : 'text-gray-900'
                } group flex w-full items-center px-6 py-2 text-base font-medium gap-2`}
              >
                <img src={LockSVG} alt="logout" /> {t('Change password')}
              </button>
            )}
          </Menu.Item>
          <Menu.Item>
            {({ active }) => (
              <button
                onClick={onLogout}
                className={`${
                  active ? `bg-gray` : 'text-gray-900'
                } group flex w-full items-center px-6 py-2 text-base font-medium gap-2`}
              >
                <img src={LogoutSVG} alt="logout" /> {t('header.logout')}
              </button>
            )}
          </Menu.Item>
          <Menu.Item>
            {({ close }) => (
              <div className="flex w-full px-6 pt-4 text-base font-medium border-t border-gray-100">
                <Switch
                  label="Demo"
                  isToggled={settings.isDemoMode}
                  onToggle={(state) => {
                    close();
                    handleDemo(state);
                  }}
                />
              </div>
            )}
          </Menu.Item>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export const ProfileAvatarIcon = () => {
  const t: any = useTheme();
  const theme = t.current;

  // color of logo will be changed based on the theme, designer has used a different color here :'(
  const fillColor = profileAvatarColorMatrix[theme as Theme];

  return (
    <svg
      id="icon_user"
      xmlns="http://www.w3.org/2000/svg"
      width="40"
      height="40"
      viewBox="0 0 40 40"
    >
      <defs>
        <radialGradient
          id="radial-gradient"
          cx="0.5"
          cy="1.007"
          r="1.009"
          gradientUnits="objectBoundingBox"
        >
          <stop offset="0" />
          <stop offset="1" stopOpacity="0" />
        </radialGradient>
        <radialGradient
          id="radial-gradient-2"
          cx="0.5"
          cy="0.5"
          r="0.5"
          xlinkHref="#radial-gradient"
        />
        <linearGradient
          id="linear-gradient"
          x1="0.161"
          y1="0.161"
          x2="0.858"
          y2="0.858"
          gradientUnits="objectBoundingBox"
        >
          <stop offset="0" stopColor="#fff" />
          <stop offset="1" stopColor="#b1b1b1" />
        </linearGradient>
      </defs>
      <path
        id="Path_1221"
        data-name="Path 1221"
        d="M44,24A20,20,0,1,1,24,4,20,20,0,0,1,44,24Z"
        transform="translate(-4 -4)"
        fill={fillColor}
      />
      <path
        id="Path_1222"
        data-name="Path 1222"
        d="M24,44a19.925,19.925,0,0,0,14.651-6.418,15.992,15.992,0,0,0-29.3,0A19.925,19.925,0,0,0,24,44Z"
        transform="translate(-4 -4)"
        fill="url(#radial-gradient)"
      />
      <circle
        id="Ellipse_85"
        data-name="Ellipse 85"
        cx="9.5"
        cy="9.5"
        r="9.5"
        transform="translate(10.5 6.5)"
        fill="url(#radial-gradient-2)"
      />
      <circle
        id="Ellipse_86"
        data-name="Ellipse 86"
        cx="8"
        cy="8"
        r="8"
        transform="translate(12 7)"
        fill="url(#linear-gradient)"
      />
      <path
        id="Path_1223"
        data-name="Path 1223"
        d="M24,44a19.9,19.9,0,0,0,13.1-4.909,13.981,13.981,0,0,0-26.2,0A19.9,19.9,0,0,0,24,44Z"
        transform="translate(-4 -4)"
        fill="url(#linear-gradient)"
      />
    </svg>
  );
};
